import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import DirtyDot from "./DirtyDot";

// label is required, not decorative: the switch's only child is a styling span, so
// without it a screen reader reads six identical "switch, on" rows and an operator
// cannot tell which flag they are flipping on a live tenant.
const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out"
    style={{ backgroundColor: checked ? "var(--color-mint)" : "#d1d5db" }}
  >
    <span
      className="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition duration-200 ease-in-out"
      style={{ transform: checked ? "translateX(20px)" : "translateX(2px)", marginTop: "2px" }}
    />
  </button>
);

const FeaturesSection = ({ features, baseline, onChange }) => {
  const { translate } = useLanguage();
  const f = features || {};
  const label = (key, fallback) => translate(key) || fallback;

  const toggle = (key) => {
    onChange({ ...f, [key]: !f[key] });
  };

  const isDirty = JSON.stringify(features || {}) !== JSON.stringify(baseline || {});

  return (
    <div className="rounded-xl overflow-hidden"
      style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)" }}>
      <div className="px-6 py-4" style={{ borderBottom: "1px solid var(--color-border)" }}>
        <h2 className="flex items-center gap-2 text-base font-semibold" style={{ color: "var(--color-text-primary)" }}>
          {translate("features") || "Features"}
          {isDirty && <DirtyDot />}
        </h2>
      </div>
      <div className="px-6 py-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
              {label("fileUpload", "File upload")}
            </p>
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {translate("fileUploadDescription") || "Allow users to attach files in conversation."}
            </p>
          </div>
          <Toggle
            checked={f.fileUpload !== false}
            onChange={() => toggle("fileUpload")}
            label={label("fileUpload", "File upload")}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
              {label("audioMessages", "Audio messages")}
            </p>
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {translate("audioMessagesDescription") || "Allow users to record and send voice notes."}
            </p>
          </div>
          <Toggle
            checked={!!f.audioMessages}
            onChange={() => toggle("audioMessages")}
            label={label("audioMessages", "Audio messages")}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
              {label("productCardDelivery", "Delivery time on product cards")}
            </p>
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {translate("productCardDeliveryDescription") ||
                "Shows the delivery estimate on each product card, or the availability state when the product cannot ship yet."}
            </p>
          </div>
          <Toggle
            checked={!!f.productCardDelivery}
            onChange={() => toggle("productCardDelivery")}
            label={label("productCardDelivery", "Delivery time on product cards")}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
              {label("productCardTagBadges", "Tag badge on product cards")}
            </p>
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {translate("productCardTagBadgesDescription") ||
                'Shows one campaign or product-state tag ("Pré-Venda", "Artigo de Exposição") on the card\'s top edge.'}
            </p>
          </div>
          <Toggle
            checked={!!f.productCardTagBadges}
            onChange={() => toggle("productCardTagBadges")}
            label={label("productCardTagBadges", "Tag badge on product cards")}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
              {label("productCardDiscountBadge", "Discount badge on product cards")}
            </p>
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {translate("productCardDiscountBadgeDescription") ||
                "Shows the discount percentage over the product photo."}
            </p>
          </div>
          <Toggle
            checked={!!f.productCardDiscountBadge}
            onChange={() => toggle("productCardDiscountBadge")}
            label={label("productCardDiscountBadge", "Discount badge on product cards")}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
              {label("productCardHoverImage", "Second photo on hover")}
            </p>
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {translate("productCardHoverImageDescription") ||
                "Shows the product's second photo while the pointer rests on the card (desktop only)."}
            </p>
          </div>
          <Toggle
            checked={!!f.productCardHoverImage}
            onChange={() => toggle("productCardHoverImage")}
            label={label("productCardHoverImage", "Second photo on hover")}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
              {label("productCardViewCta", "\"View product\" button on cards")}
            </p>
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {translate("productCardViewCtaDescription") ||
                "Adds a full-width button at the foot of the card, opening the product page. Hidden while the add-to-cart button is on."}
            </p>
          </div>
          <Toggle
            checked={!!f.productCardViewCta}
            onChange={() => toggle("productCardViewCta")}
            label={label("productCardViewCta", "\"View product\" button on cards")}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
              {label("cardLinksSameTab", "Open recommendations in the same page")}
            </p>
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {translate("cardLinksSameTabDescription") ||
                "Product and curated cards navigate the current page instead of opening a new tab, and the chat reopens over it. Only applies to links on your own site (on phones the chat reopens with one tap on the launcher)."}
            </p>
          </div>
          <Toggle
            checked={!!f.cardLinksSameTab}
            onChange={() => toggle("cardLinksSameTab")}
            label={label("cardLinksSameTab", "Open recommendations in the same page")}
          />
        </div>
      </div>
    </div>
  );
};

export default FeaturesSection;
